// 涨跌方向色只有一个来源：styles/tokens.css 的 --semantic-up / --semantic-down。
// 本文件里不许再写死十六进制——2026-09-02 方向从绿涨红跌改回红涨绿跌时，这里 8 处
// 写死的颜色没跟上，行业 K 线和个股 K 线一度方向相反（同一个站点两套语义），
// 而分时成交量那处又是第三种。由 tests/test_frontend_contracts.py 的涨跌色契约执法。
// 多序列对象色（industryChartOption / marketSeriesChartOption 的 colors 数组）和
// 单序列价格线不是方向色，保持原样。
// 单序列价格线用的品牌绿，**和涨跌方向无关**（那条线不论当日涨跌都是这个颜色，
// 方向由 tooltip 里的数字和成交量柱表达）。提成常量是为了让方向色契约能分辨
// "这一处的绿不是涨色"——它和 --semantic-up/down 不是一回事，方向翻转时不要动它。
const PRICE_LINE_COLOR = "#00864a";

function cssTokens() {
  // node --test 里没有 document/getComputedStyle：这些 option 构造器是纯函数，
  // 单测直接调用它们。取不到就退回令牌当前值（浏览器里永远取得到，兜底只在测试生效）。
  return typeof document === "undefined" || typeof getComputedStyle !== "function"
    ? null
    : getComputedStyle(document.documentElement);
}

export function directionColors() {
  const css = cssTokens();
  return {
    up: css?.getPropertyValue("--semantic-up").trim() || "#d63f47",
    down: css?.getPropertyValue("--semantic-down").trim() || "#00966a",
  };
}

function number(value, digits = 2) {
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? parsed.toLocaleString("zh-CN", { maximumFractionDigits: digits, minimumFractionDigits: digits })
    : "--";
}

function signed(value, suffix = "%", digits = 2) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "--";
  return `${parsed > 0 ? "+" : ""}${parsed.toFixed(digits)}${suffix}`;
}

export function distributionOption(distribution) {
  const { up, down } = directionColors();
  const labels = ["涨停10%+", "7%~10%", "3%~7%", "0%~3%", "平盘", "0%~-3%", "-3%~-7%", "-7%~-10%", "跌停-10%"];
  const values = [distribution.ge_10, distribution.p7_10, distribution.p3_7, distribution.p0_3, distribution.flat, distribution.n0_3, distribution.n3_7, distribution.n7_10, distribution.le_n10];
  return {
    grid: { left: 68, right: 40, top: 8, bottom: 8 },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    yAxis: { type: "category", data: labels, inverse: true, axisLabel: { color: "#667085", fontSize: 11 }, axisLine: { lineStyle: { color: "#dfe7e2" } } },
    xAxis: { type: "value", axisLabel: { color: "#98a2b3" }, splitLine: { lineStyle: { color: "#edf2ee" } } },
    series: [{
      type: "bar",
      data: values.map((value, index) => ({ value, itemStyle: { color: index < 4 ? up : index === 4 ? "#98a2b3" : down, borderRadius: [0, 4, 4, 0] } })),
      barMaxWidth: 18,
      label: { show: true, position: "right", color: "#667085", fontSize: 11 },
    }],
  };
}

export function lineOption(series, valueKey, name, color, unit, secondaryKey = null, secondaryName = null) {
  const chartValue = (item, key) => item[key] === null || item[key] === undefined ? null : Number(item[key]);
  const chartSeries = [{ name, type: "line", showSymbol: false, smooth: 0.18, connectNulls: false, data: series.map((item) => chartValue(item, valueKey)), lineStyle: { color, width: 2 }, areaStyle: { color: `${color}14` } }];
  if (secondaryKey) chartSeries.push({ name: secondaryName, type: "line", showSymbol: false, connectNulls: false, data: series.map((item) => chartValue(item, secondaryKey)), lineStyle: { color: "#98a2b3", width: 1.3, type: "dashed" } });
  return {
    grid: { left: 56, right: 24, top: 34, bottom: 38 },
    legend: { top: 0, right: 0, textStyle: { color: "#667085" } },
    tooltip: { trigger: "axis", valueFormatter: (value) => `${number(value, 1)} ${unit}` },
    xAxis: { type: "category", data: series.map((item) => item.date), boundaryGap: false, axisLabel: { color: "#98a2b3", hideOverlap: true }, axisLine: { lineStyle: { color: "#dfe7e2" } } },
    yAxis: { type: "value", scale: true, axisLabel: { color: "#98a2b3" }, splitLine: { lineStyle: { color: "#edf2ee" } } },
    series: chartSeries,
  };
}

export function barOption(series, valueKey, name) {
  const { up, down } = directionColors();
  return {
    grid: { left: 54, right: 18, top: 30, bottom: 38 },
    tooltip: { trigger: "axis", valueFormatter: (value) => signed(value, " 亿", 1) },
    xAxis: { type: "category", data: series.map((item) => item.date), axisLabel: { color: "#98a2b3", hideOverlap: true }, axisLine: { lineStyle: { color: "#dfe7e2" } } },
    yAxis: { type: "value", axisLabel: { color: "#98a2b3" }, splitLine: { lineStyle: { color: "#edf2ee" } } },
    series: [{ name, type: "bar", data: series.map((item) => ({ value: Number(item[valueKey]), itemStyle: { color: Number(item[valueKey]) >= 0 ? up : down, borderRadius: Number(item[valueKey]) >= 0 ? [3, 3, 0, 0] : [0, 0, 3, 3] } })), barMaxWidth: 12 }],
  };
}

export function industryChartOption(payloads, compareMode) {
  const { up: klineUp, down: klineDown } = directionColors();
  if (compareMode) {
    const colors = ["#e5484d", "#2563eb", "#7c3aed", "#f97316"];
    const allDates = [...new Set(payloads.flatMap((payload) => payload.series.map((item) => item.date)))].sort();
    const series = payloads.map((payload, index) => {
      const values = new Map(payload.series.map((item) => [item.date, Number(item.close)]));
      const first = payload.series.find((item) => Number(item.close))?.close;
      return {
        name: payload.industry.industry_name, type: "line", showSymbol: false, connectNulls: true,
        data: allDates.map((date) => values.has(date) && Number(first) ? (values.get(date) / Number(first) - 1) * 100 : null),
        lineStyle: { width: 2, color: colors[index] },
      };
    });
    return {
      grid: { left: 60, right: 24, top: 42, bottom: 48 },
      legend: { top: 0, textStyle: { color: "#667085" } },
      tooltip: { trigger: "axis", valueFormatter: (value) => signed(value) },
      xAxis: { type: "category", data: allDates, boundaryGap: false, axisLabel: { color: "#98a2b3", hideOverlap: true }, axisLine: { lineStyle: { color: "#dfe7e2" } } },
      yAxis: { type: "value", axisLabel: { color: "#98a2b3", formatter: "{value}%" }, splitLine: { lineStyle: { color: "#edf2ee" } } },
      series,
    };
  }
  const points = payloads[0].series;
  return {
    grid: [{ left: 62, right: 24, top: 28, height: "62%" }, { left: 62, right: 24, top: "76%", height: "15%" }],
    tooltip: { trigger: "axis", axisPointer: { type: "cross" } },
    xAxis: [
      { type: "category", data: points.map((item) => item.date), boundaryGap: true, axisLabel: { color: "#98a2b3", hideOverlap: true }, axisLine: { lineStyle: { color: "#dfe7e2" } } },
      { type: "category", gridIndex: 1, data: points.map((item) => item.date), axisLabel: { show: false }, axisLine: { show: false }, axisTick: { show: false } },
    ],
    yAxis: [
      { type: "value", scale: true, axisLabel: { color: "#98a2b3" }, splitLine: { lineStyle: { color: "#edf2ee" } } },
      { type: "value", gridIndex: 1, axisLabel: { show: false }, splitLine: { show: false } },
    ],
    series: [
      { name: "行业指数", type: "candlestick", data: points.map((item) => [Number(item.open), Number(item.close), Number(item.low), Number(item.high)]), itemStyle: { color: klineUp, color0: klineDown, borderColor: klineUp, borderColor0: klineDown } },
      { name: "成交量", type: "bar", xAxisIndex: 1, yAxisIndex: 1, data: points.map((item) => Number(item.volume)), itemStyle: { color: "#cbd5e1" }, barMaxWidth: 10 },
    ],
    dataZoom: [{ type: "inside", xAxisIndex: [0, 1], start: 35, end: 100 }, { type: "slider", xAxisIndex: [0, 1], bottom: 0, height: 18, start: 35, end: 100 }],
  };
}

function movingAverage(values, windowSize) {
  return values.map((value, index) => {
    if (index + 1 < windowSize) return null;
    const window = values.slice(index + 1 - windowSize, index + 1);
    if (window.some((item) => !Number.isFinite(item))) return null;
    return window.reduce((sum, item) => sum + item, 0) / windowSize;
  });
}

function formatRawValue(value, unit) {
  if (unit === "percent") return signed(value);
  const unitLabels = { CNY: "元", index_point: "点" };
  return `${number(value, 2)} ${unitLabels[unit] ?? unit ?? ""}`.trim();
}

// Live-intraday volume is reported in shares (股); the reference display is
// in lots (手 = 100 股), switching to 万手 once it gets into the millions.
function formatShareVolume(shares) {
  if (!Number.isFinite(shares)) return "--";
  const lots = shares / 100;
  return lots >= 10000 ? `${(lots / 10000).toFixed(1)}万手` : `${Math.round(lots)}手`;
}

const A_SHARE_INTRADAY_SESSIONS = [
  ["09:30", "11:30"],
  ["13:00", "15:00"],
];

function minuteOfDay(value) {
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

export function aShareIntradayMinuteLabels() {
  return A_SHARE_INTRADAY_SESSIONS.flatMap(([start, end]) => {
    const labels = [];
    for (let minute = minuteOfDay(start); minute <= minuteOfDay(end); minute += 1) {
      const hours = String(Math.floor(minute / 60)).padStart(2, "0");
      const minutes = String(minute % 60).padStart(2, "0");
      labels.push(`${hours}:${minutes}`);
    }
    return labels;
  });
}

function intradayMinuteLabel(timestamp) {
  const match = String(timestamp ?? "").match(/(?:^|[T\s])(\d{2}):(\d{2})(?::\d{2})?/);
  return match ? `${match[1]}:${match[2]}` : String(timestamp ?? "");
}

function alignIntradayPoints(points, timestamps) {
  const pointsByMinute = new Map(
    points.map((point) => [intradayMinuteLabel(point.t), point]),
  );
  return timestamps.map((timestamp) => pointsByMinute.get(timestamp) ?? null);
}

export function marketSeriesChartOption(payload, period, previousClose = null) {
  const { up: seriesUp, down: seriesDown } = directionColors();
  const entries = payload?.data?.series ?? [];
  if (!entries.length) return {};
  const compareMode = entries.length > 1;
  const colors = ["#00864a", "#2563eb", "#7c3aed"];

  if (compareMode) {
    const timestamps = period === "intraday"
      ? aShareIntradayMinuteLabels()
      : [...new Set(
          entries.flatMap((entry) => entry.points.map((point) => point.t)),
        )].sort();
    return {
      animationDuration: 260,
      grid: { left: 58, right: 24, top: 44, bottom: 42 },
      legend: { top: 0, type: "scroll", textStyle: { color: "#667085", fontSize: 11 } },
      tooltip: {
        trigger: "axis",
        formatter: (params) => {
          const rows = params.map((item) => {
            const data = item.data ?? {};
            return `${item.marker}${item.seriesName}：${number(data.value, 2)}`
              + `<br/><span style="padding-left:16px;color:#98a2b3">原始 ${formatRawValue(data.raw, data.unit)}</span>`;
          });
          return `${params[0]?.axisValueLabel ?? ""}<br/>${rows.join("<br/>")}`;
        },
      },
      xAxis: {
        type: "category",
        data: timestamps,
        boundaryGap: false,
        axisLabel: {
          color: "#98a2b3",
          hideOverlap: true,
          showMinLabel: period === "intraday",
          showMaxLabel: period === "intraday",
        },
        axisLine: { lineStyle: { color: "#dfe7e2" } },
      },
      yAxis: {
        type: "value",
        scale: true,
        name: "共同起点 = 100",
        nameTextStyle: { color: "#98a2b3" },
        axisLabel: { color: "#98a2b3" },
        splitLine: { lineStyle: { color: "#edf2ee" } },
      },
      series: entries.map((entry, index) => {
        const points = new Map(entry.points.map((point) => [
          period === "intraday" ? intradayMinuteLabel(point.t) : point.t,
          point,
        ]));
        const base = Number(entry.points[0]?.raw);
        return {
          name: entry.name,
          type: "line",
          showSymbol: false,
          connectNulls: period === "intraday",
          lineStyle: { width: 2, color: colors[index] },
          data: timestamps.map((timestamp) => {
            const point = points.get(timestamp);
            if (!point) return null;
            const normalized = Number(point.normalized ?? (Number(point.raw) / base) * 100);
            return {
              value: normalized,
              raw: Number(point.raw),
              unit: entry.unit,
            };
          }),
        };
      }),
    };
  }

  const entry = entries[0];
  const points = entry.points;
  const timestamps = period === "intraday"
    ? aShareIntradayMinuteLabels()
    : points.map((point) => point.t);
  const axisPoints = period === "intraday"
    ? alignIntradayPoints(points, timestamps)
    : points;
  const values = axisPoints.map((point) => point ? Number(point.raw) : null);
  const showMovingAverages = period !== "intraday";
  // An industry has no meaningful per-minute share volume of its own (its
  // live feed only ever carries a recomputed pct_chg/level, never a volume
  // figure) -- rendering an always-empty volume subplot for it just leaves a
  // dead axis and stray label space, so drop the whole second grid instead.
  const validVolumePointCount = points.filter(
    (point) => point.volume !== null
      && point.volume !== undefined
      && Number(point.volume) > 0,
  ).length;
  // One isolated bucket leaves a large, almost empty subplot and usually
  // means the intraday task started late. Wait for a continuous pair before
  // exposing the volume panel; the price line remains available meanwhile.
  const hasVolumeData = validVolumePointCount >= 2;
  // The intraday reference (jiaoyi.qq.com/雪球-style) view pairs the raw price
  // with a dashed 昨收 line and a twin percent axis so the same movement can
  // be read as either an absolute level or a % change without a second chart.
  const hasReference = period === "intraday" && entry.unit !== "percent"
    && Number.isFinite(previousClose) && previousClose !== 0;
  const lineSeries = [
    {
      name: entry.name,
      type: "line",
      showSymbol: false,
      connectNulls: period === "intraday",
      smooth: period === "intraday" ? 0.08 : 0.16,
      data: values,
      lineStyle: { color: PRICE_LINE_COLOR, width: 2 },
      areaStyle: period === "intraday" ? { color: `${PRICE_LINE_COLOR}12` } : undefined,
      markLine: hasReference
        ? {
            symbol: "none",
            silent: true,
            label: {
              formatter: `昨收 ${number(previousClose)}`,
              position: "insideEndTop",
              distance: 6,
              color: "#98a2b3",
              backgroundColor: "#ffffffd9",
              padding: [2, 4],
            },
            lineStyle: { type: "dashed", color: "#98a2b3" },
            data: [{ yAxis: previousClose }],
          }
        : undefined,
    },
  ];
  if (showMovingAverages) {
    [
      [5, "#2563eb"],
      [10, "#7c3aed"],
      [20, "#f97316"],
    ].forEach(([windowSize, color]) => {
      lineSeries.push({
        name: `MA${windowSize}`,
        type: "line",
        showSymbol: false,
        data: movingAverage(values, windowSize),
        lineStyle: { color, width: 1.2 },
      });
    });
  }
  let priceAxis = {
    type: "value",
    scale: true,
    axisLabel: { color: "#98a2b3" },
    splitLine: { lineStyle: { color: "#edf2ee" } },
  };
  let percentAxis = null;
  if (hasReference) {
    const finiteValues = values.filter(Number.isFinite);
    const rawMin = Math.min(...finiteValues, previousClose);
    const rawMax = Math.max(...finiteValues, previousClose);
    const largestMove = Math.max(previousClose - rawMin, rawMax - previousClose);
    const halfSpan = largestMove > 0 ? largestMove * 1.12 : previousClose * 0.005;
    const axisMin = previousClose - halfSpan;
    const axisMax = previousClose + halfSpan;
    priceAxis = {
      ...priceAxis,
      min: axisMin,
      max: axisMax,
      axisLabel: { ...priceAxis.axisLabel, formatter: (value) => Number(value).toFixed(2) },
    };
    // Right-hand percent axis is derived from the same padded price bounds,
    // so its gridlines line up exactly with the price axis (both linear
    // transforms of one anchor) instead of two independently auto-scaled axes.
    percentAxis = {
      type: "value",
      gridIndex: 0,
      position: "right",
      min: (axisMin / previousClose - 1) * 100,
      max: (axisMax / previousClose - 1) * 100,
      axisLabel: {
        color: "#98a2b3",
        formatter: (value) => `${value.toFixed(2)}%`,
        hideOverlap: true,
        margin: 12,
      },
      splitLine: { show: false },
    };
  }
  const zoomAxisIndex = hasVolumeData ? [0, 1] : [0];
  return {
    animationDuration: 260,
    grid: hasVolumeData
      ? [
          { left: 62, right: hasReference ? 72 : 24, top: 42, height: "59%" },
          { left: 62, right: hasReference ? 72 : 24, top: "77%", height: "13%" },
        ]
      : [{ left: 62, right: hasReference ? 72 : 24, top: 42, bottom: period === "intraday" ? 34 : 60 }],
    legend: { top: 0, type: "scroll", textStyle: { color: "#667085", fontSize: 11 } },
    tooltip: hasReference
      ? {
          trigger: "axis",
          extraCssText: "line-height: 1.6;",
          formatter: (params) => {
            const priceItem = params.find((item) => item.seriesName === entry.name);
            if (priceItem?.data === null || priceItem?.data === undefined) return "";
            const price = Number(priceItem.data);
            if (!Number.isFinite(price)) return "";
            const pct = (price / previousClose - 1) * 100;
            // The volume bar lives on a separate grid/xAxisIndex (its own
            // subplot below the price chart), so an axis-trigger tooltip
            // fired from the price grid never receives it via `params` --
            // read it straight from the point at the same index instead.
            const rawVolume = axisPoints[priceItem.dataIndex]?.volume;
            const volumeValue = rawVolume === null || rawVolume === undefined
              ? null
              : Number(rawVolume);
            const rows = [
              `${priceItem.marker}${entry.name} `
                + `<b style="font-size:14px;color:${pct >= 0 ? seriesUp : seriesDown}">`
                + `${formatRawValue(price, entry.unit)} ${signed(pct)}</b>`,
              `<span style="color:#98a2b3">昨收 ${number(previousClose)}</span>`,
            ];
            if (Number.isFinite(volumeValue)) rows.push(`分钟量 ${formatShareVolume(volumeValue)}`);
            return `${priceItem.axisValueLabel}<br/>${rows.join("<br/>")}`;
          },
        }
      : {
          trigger: "axis",
          valueFormatter: (value) => formatRawValue(value, entry.unit),
        },
    xAxis: [
      {
        type: "category",
        data: timestamps,
        boundaryGap: false,
        axisLabel: {
          color: "#98a2b3",
          hideOverlap: true,
          showMinLabel: period === "intraday",
          showMaxLabel: period === "intraday",
        },
        axisLine: { lineStyle: { color: "#dfe7e2" } },
      },
      ...(hasVolumeData
        ? [
            {
              type: "category",
              gridIndex: 1,
              data: timestamps,
              axisLabel: { show: false },
              axisLine: { show: false },
              axisTick: { show: false },
            },
          ]
        : []),
    ],
    yAxis: [
      priceAxis,
      ...(hasVolumeData
        ? [{ type: "value", gridIndex: 1, axisLabel: { show: false }, splitLine: { show: false } }]
        : []),
      ...(percentAxis ? [percentAxis] : []),
    ],
    series: [
      ...lineSeries,
      ...(hasVolumeData
        ? [
            {
              name: "成交量",
              type: "bar",
              xAxisIndex: 1,
              yAxisIndex: 1,
              data: (() => {
                let previousPrice = previousClose;
                return axisPoints.map((point) => {
                  if (!point) return null;
                  if (point.volume === null || point.volume === undefined) {
                    previousPrice = Number(point.raw);
                    return null;
                  }
                  const volume = Number(point.volume ?? 0);
                  if (!hasReference) return volume;
                  const currentPrice = Number(point.raw);
                  let color = "#98a2b399";
                  if (!Number.isFinite(previousPrice) || !Number.isFinite(currentPrice)) {
                    color = "#98a2b399";
                  } else if (currentPrice > previousPrice) {
                    color = `${seriesUp}99`;
                  } else if (currentPrice < previousPrice) {
                    color = `${seriesDown}99`;
                  }
                  previousPrice = currentPrice;
                  return { value: volume, itemStyle: { color } };
                });
              })(),
              itemStyle: { color: "#cbd5e1" },
              barMaxWidth: period === "intraday" ? 5 : 10,
            },
          ]
        : []),
    ],
    dataZoom: period === "intraday"
      ? []
      : [
          { type: "inside", xAxisIndex: zoomAxisIndex, start: 35, end: 100 },
          { type: "slider", xAxisIndex: zoomAxisIndex, bottom: 0, height: 18, start: 35, end: 100 },
        ],
  };
}

export function instrumentKlineOption(payload, period) {
  const { up: seriesUp, down: seriesDown } = directionColors();
  const entries = payload?.data?.series ?? [];
  const entry = entries[0];
  const points = entry?.points ?? [];
  if (entries.length > 1 || !points.length || points[0].open === undefined || points[0].open === null) {
    return marketSeriesChartOption(payload, period);
  }
  const dates = points.map((point) => point.t);
  const closes = points.map((point) => Number(point.raw));
  const hasOhlc = (point) => [point.open, point.high, point.low, point.raw]
    .every((value) => value !== null && value !== undefined && Number.isFinite(Number(value)));
  const maSeries = [
    [5, "#2563eb"],
    [10, "#7c3aed"],
    [20, "#f97316"],
  ].map(([windowSize, color]) => ({
    name: `MA${windowSize}`,
    type: "line",
    showSymbol: false,
    data: movingAverage(closes, windowSize),
    lineStyle: { color, width: 1.2 },
  }));
  return {
    animationDuration: 260,
    grid: [
      { left: 62, right: 24, top: 42, height: "62%" },
      { left: 62, right: 24, top: "80%", height: "14%" },
    ],
    legend: { top: 0, type: "scroll", textStyle: { color: "#667085", fontSize: 11 } },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross" },
      textStyle: { fontSize: 12 },
      formatter: (params) => {
        const candle = params.find((item) => item.seriesType === "candlestick");
        const closeOnly = params.find((item) => item.seriesName === "仅收盘");
        if (!candle && closeOnly) {
          return `${closeOnly.axisValueLabel}<br/>收盘 ${number(closeOnly.data)}`;
        }
        if (!candle) return "";
        const [open, close, low, high] = candle.data.slice(1);
        const rows = [
          `开 ${number(open)}　收 ${number(close)}`,
          `低 ${number(low)}　高 ${number(high)}`,
          ...params.filter((item) => item.seriesType === "line" && item.data !== null)
            .map((item) => `${item.seriesName} ${number(item.data)}`),
        ];
        return `${candle.axisValueLabel}<br/>${rows.join("<br/>")}`;
      },
    },
    xAxis: [
      { type: "category", data: dates, boundaryGap: true, axisLabel: { color: "#98a2b3", hideOverlap: true }, axisLine: { lineStyle: { color: "#dfe7e2" } } },
      { type: "category", gridIndex: 1, data: dates, axisLabel: { show: false }, axisLine: { show: false }, axisTick: { show: false } },
    ],
    yAxis: [
      { type: "value", scale: true, axisLabel: { color: "#98a2b3" }, splitLine: { lineStyle: { color: "#edf2ee" } } },
      { type: "value", gridIndex: 1, axisLabel: { show: false }, splitLine: { show: false } },
    ],
    series: [
      {
        name: entry.name,
        type: "candlestick",
        data: points.map((point) => hasOhlc(point)
          ? [Number(point.open), Number(point.raw), Number(point.low), Number(point.high)]
          : "-"),
        itemStyle: { color: seriesUp, color0: seriesDown, borderColor: seriesUp, borderColor0: seriesDown },
      },
      {
        name: "仅收盘",
        type: "scatter",
        symbol: "diamond",
        symbolSize: 8,
        data: points.map((point) => hasOhlc(point) ? null : Number(point.raw)),
        itemStyle: { color: "#667085" },
      },
      ...maSeries,
      {
        name: "成交量",
        type: "bar",
        xAxisIndex: 1,
        yAxisIndex: 1,
        data: points.map((point) => ({
          value: Number(point.volume ?? 0),
          itemStyle: {
            color: hasOhlc(point) && Number(point.raw) < Number(point.open)
              ? seriesDown
              : seriesUp,
          },
        })),
        barMaxWidth: 10,
      },
    ],
    dataZoom: [{ type: "inside", xAxisIndex: [0, 1], start: 35, end: 100 }],
  };
}

export function industryActivityChartOption(payload, metric) {
  const colors = ["#0f766e", "#2563eb", "#7c3aed", "#ea580c", "#db2777", "#4d7c0f", "#0891b2", "#9333ea", "#c2410c", "#be123c", "#047857", "#1d4ed8"];
  const seriesEntries = payload?.series ?? [];
  const dates = [...new Set(seriesEntries.flatMap((item) => item.points.map((point) => point.date)))].sort();
  const metricMeta = {
    market_turnover_share: ["成交额占比", "%"],
    share_change_10d_pp: ["十日份额变化", " pp"],
    equal_weight_pct_chg_5d: ["5日等权涨跌", "%"],
    excess_pct_chg_5d: ["相对沪深300超额", " pp"],
    volume_ratio_20d: ["20日量比", "x"],
    up_ratio: ["上涨家数占比", "%"],
  };
  const [metricName, unit] = metricMeta[metric] ?? metricMeta.market_turnover_share;
  return {
    animationDuration: 280,
    grid: { left: 58, right: 24, top: 54, bottom: 44 },
    legend: { top: 0, type: "scroll", textStyle: { color: "#667085", fontSize: 11 } },
    tooltip: {
      trigger: "axis",
      valueFormatter: (value) => `${number(value, metric === "activity_index" ? 1 : 2)}${unit}`,
    },
    xAxis: {
      type: "category", data: dates, boundaryGap: false,
      axisLabel: { color: "#98a2b3", hideOverlap: true },
      axisLine: { lineStyle: { color: "#dfe7e2" } },
    },
    yAxis: {
      type: "value", scale: true, name: metricName, nameTextStyle: { color: "#98a2b3" },
      axisLabel: { color: "#98a2b3", formatter: `{value}${unit}` },
      splitLine: { lineStyle: { color: "#edf2ee" } },
    },
    series: seriesEntries.map((item, index) => {
      const values = new Map(item.points.map((point) => [point.date, point.value]));
      return {
        name: item.name,
        type: "line",
        showSymbol: false,
        connectNulls: false,
        data: dates.map((date) => values.get(date) ?? null),
        lineStyle: { width: 2, color: colors[index % colors.length] },
        itemStyle: { color: colors[index % colors.length] },
        emphasis: { focus: "series" },
      };
    }),
  };
}
